import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  HostedAgent,
  HostedAgentAlert,
  HostedAgentAlertKind,
  HostedAgentAlertSeverity,
} from "@/api/admin-api"

import { Bell, Central, Check } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"

import {
  useAcknowledgeHostedAgentAlertMutation,
  useAdminHostedAgentAlertsQuery,
} from "../hooks/admin-queries"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

/**
 * Open operational alerts (#3039) — budget thresholds crossed, caps
 * exceeded, agents gone silent — with a one-click acknowledge. Rendered
 * above the agents table so an admin sees what needs attention before the
 * list. Acknowledging closes the alert; a recurrence raises a new one.
 */

const KIND_DEFAULTS: Record<HostedAgentAlertKind, string> = {
  budget_threshold: "Budget warning",
  budget_exceeded: "Budget exceeded",
  agent_unreachable: "Agent unreachable",
  run_failures: "Run failures",
}

const SEVERITY_DEFAULTS: Record<HostedAgentAlertSeverity, string> = {
  warning: "Warning",
  critical: "Critical",
}

const WINDOW_DEFAULTS: Record<string, string> = {
  daily_tokens: "tokens per day",
  monthly_tokens: "tokens per month",
  monthly_sek: "SEK per month",
}

const UNIT_DEFAULTS: Record<string, string> = { tokens: "tokens", sek: "SEK" }

export function HostedAgentAlertsCard({ agents }: { agents: HostedAgent[] }) {
  const { t, i18n } = useTranslation()
  const alertsQuery = useAdminHostedAgentAlertsQuery()
  const ackMutation = useAcknowledgeHostedAgentAlertMutation()
  const numberFormat = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 })
  const alerts = alertsQuery.data?.alerts ?? []

  const agentName = (agentId: string) =>
    agents.find((agent) => agent.id === agentId)?.profile.name ??
    t("adminDashboard.hostedAgents.alerts.unknownAgent", { defaultValue: "Unknown agent" })

  const describeFacts = (alert: HostedAgentAlert): string | null => {
    const { window, unit, percent, limit, resets_at: resetsAt } = alert.facts
    if (typeof percent !== "number" || typeof limit !== "number") return null
    const unitKey = typeof unit === "string" ? unit : "tokens"
    const parts = [
      typeof window === "string"
        ? t(`adminDashboard.hostedAgents.alerts.windows.${window}`, {
            defaultValue: WINDOW_DEFAULTS[window] ?? window,
          })
        : null,
      t("adminDashboard.hostedAgents.alerts.budgetFacts", {
        defaultValue: "{{percent}} % of {{limit}} {{unit}}",
        percent: numberFormat.format(percent),
        limit: numberFormat.format(limit),
        unit: t(`adminDashboard.hostedAgents.alerts.units.${unitKey}`, {
          defaultValue: UNIT_DEFAULTS[unitKey] ?? unitKey,
        }),
      }),
      typeof resetsAt === "string"
        ? t("adminDashboard.hostedAgents.alerts.resetsAt", {
            defaultValue: "resets {{date}}",
            date: formatHostedAgentTimestamp(resetsAt, i18n.language) ?? resetsAt,
          })
        : null,
    ]
    return parts.filter(Boolean).join(" · ")
  }

  const handleAcknowledge = async (alert: HostedAgentAlert) => {
    try {
      await ackMutation.mutateAsync(alert.id)
      toast.success(
        t("adminDashboard.hostedAgents.alerts.ackSuccess", { defaultValue: "Alert acknowledged" })
      )
    } catch (error) {
      toast.error(
        t("adminDashboard.hostedAgents.alerts.ackError", {
          defaultValue: "Failed to acknowledge alert",
        }),
        { description: error instanceof Error ? error.message : undefined }
      )
    }
  }

  return (
    <section
      aria-labelledby="hosted-agent-alerts-title"
      className="mb-4 rounded-lg border"
      data-testid="hosted-agent-alerts-card"
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Bell className="text-muted-foreground size-4" />
          <h3 className="text-sm font-medium" id="hosted-agent-alerts-title">
            {t("adminDashboard.hostedAgents.alerts.title", { defaultValue: "Alerts" })}
          </h3>
          {alerts.length > 0 ? (
            <Badge
              data-testid="hosted-agent-alerts-count"
              variant={alerts.some((alert) => alert.severity === "critical") ? "error" : "warning"}
            >
              {alerts.length}
            </Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground hidden text-xs sm:block">
          {t("adminDashboard.hostedAgents.alerts.description", {
            defaultValue: "Budget thresholds and agent health. Acknowledging closes an alert.",
          })}
        </p>
      </div>

      {alertsQuery.isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : null}
      {alertsQuery.isError ? (
        <p className="text-muted-foreground p-4 text-sm" data-testid="hosted-agent-alerts-error">
          {t("adminDashboard.hostedAgents.alerts.loadError", {
            defaultValue: "Alerts are not available right now.",
          })}
        </p>
      ) : null}
      {alertsQuery.isSuccess && alerts.length === 0 ? (
        <div className="flex items-center gap-3 p-4" data-testid="hosted-agent-alerts-empty">
          <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
            <Central.BellOff className="text-muted-foreground size-4" />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-medium">
              {t("adminDashboard.hostedAgents.alerts.emptyTitle", {
                defaultValue: "No open alerts",
              })}
            </p>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.hostedAgents.alerts.emptyDescription", {
                defaultValue:
                  "Budget warnings and health alerts appear here as soon as they are raised.",
              })}
            </p>
          </div>
        </div>
      ) : null}
      {alerts.length > 0 ? (
        <ul className="divide-y" data-testid="hosted-agent-alerts-list">
          {alerts.map((alert) => {
            const facts = describeFacts(alert)
            const isPending = ackMutation.isPending && ackMutation.variables === alert.id
            return (
              <li
                key={alert.id}
                className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
                data-alert-kind={alert.kind}
                data-testid={`hosted-agent-alert-${alert.id}`}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={alert.severity === "critical" ? "error" : "warning"}>
                      {t(`adminDashboard.hostedAgents.alerts.severities.${alert.severity}`, {
                        defaultValue: SEVERITY_DEFAULTS[alert.severity],
                      })}
                    </Badge>
                    <span className="text-sm font-medium">
                      {t(`adminDashboard.hostedAgents.alerts.kinds.${alert.kind}`, {
                        defaultValue: KIND_DEFAULTS[alert.kind],
                      })}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      · {agentName(alert.agent_id)}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {facts ?? alert.title}
                    {" · "}
                    {formatHostedAgentTimestamp(alert.created_at, i18n.language)}
                  </p>
                </div>
                <Button
                  className="gap-1"
                  data-testid={`hosted-agent-alert-ack-${alert.id}`}
                  disabled={isPending}
                  size="sm"
                  variant="outline"
                  onClick={() => void handleAcknowledge(alert)}
                >
                  <Check className="size-4" />
                  {isPending
                    ? t("adminDashboard.hostedAgents.alerts.acknowledging", {
                        defaultValue: "Acknowledging…",
                      })
                    : t("adminDashboard.hostedAgents.alerts.acknowledge", {
                        defaultValue: "Acknowledge",
                      })}
                </Button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </section>
  )
}
